import React, { useEffect, useState } from 'react';
import PR_LOGO from './logo-icon.png';
import RedirectFooter from './RedirectFooter';

const REDIRECT_URL = 'https://recruit.placerep.ai';
const REDIRECT_SECONDS = 10;
const MN_LOGO = `${process.env.PUBLIC_URL}/images/mp-logo.svg`;

function MasterNaukriLogo() {
  return <img className="pr-brand-logo" src={MN_LOGO} alt="masterNAUKRI" />;
}

function FeatureIcon({ children }) {
  return <span className="pr-feat-icon">{children}</span>;
}

const features = [
  {
    title: 'Same team, smarter platform',
    text: 'The team behind masterNAUKRI is now building AI-powered hiring tools to help you find and shortlist talent faster.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 2l2.39 4.84 5.35.78-3.87 3.77.91 5.32L12 14.27l-4.78 2.51.91-5.32L4.26 7.62l5.35-.78L12 2z" />
      </svg>
    ),
  },
  {
    title: 'Your recruiter account is ready',
    text: 'Sign in at recruit.placerep.ai with the same credentials. Your company profile, job posts, and candidate pipeline are intact.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
  },
  {
    title: 'Smarter sourcing & screening',
    text: 'Everything you used on masterNAUKRI — plus AI candidate matching, smart shortlisting, and richer hiring insights.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
  },
  {
    title: 'Update your bookmarks',
    text: 'Old masternaukri/portfolio links should now point to recruit.placerep.ai. We will keep this redirect live for a while.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
];

export default function Home() {
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    if (secondsLeft <= 0) {
      window.location.replace(REDIRECT_URL);
      return undefined;
    }
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  const progressPct = Math.max(0, (secondsLeft / REDIRECT_SECONDS) * 100);

  const goNow = () => window.location.replace(REDIRECT_URL);

  return (
    <div className="pr-root">
      <header className="pr-topbar">
        <div className="pr-topbar-inner">
          <MasterNaukriLogo />
          <a className="pr-topbar-cta" href={REDIRECT_URL}>
            Visit recruit.placerep.ai
            <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </header>

      <main>
        <section className="pr-hero" aria-labelledby="pr-hero-title">
          <div className="pr-hero-inner">
            <div className="pr-transition" aria-label="masterNAUKRI is now PlaceRep.ai">
              <div className="pr-transition-card">
                <img className="pr-transition-logo" src={MN_LOGO} alt="masterNAUKRI" />
              </div>
              <span className="pr-transition-text">is now</span>
              <div className="pr-transition-card pr-transition-card--new">
                <img className="pr-transition-logo" src={PR_LOGO} alt="PlaceRep.ai" />
              </div>
            </div>

            <h1 id="pr-hero-title" className="pr-h1">
              We&rsquo;ve moved to{' '}
              <span className="pr-gradient-text">a new exciting home</span>.
            </h1>

            <p className="pr-subhead">
              <strong>masterNAUKRI</strong> is now <strong>PlaceRep.ai</strong> &mdash; an
              AI-powered hiring &amp; placement platform, built for recruiters and institutions.
            </p>

            <div className="pr-card" role="status" aria-live="polite">
              <span className="pr-count-label">Redirecting in</span>
              <div className="pr-count-row">
                <span className="pr-count-num pr-font-display">{secondsLeft}</span>
                <span className="pr-count-unit">sec</span>
              </div>
              <p className="pr-count-sub">
                Taking you to <strong>recruit.placerep.ai</strong> automatically.
              </p>

              <div
                className="pr-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={REDIRECT_SECONDS}
                aria-valuenow={secondsLeft}
                aria-label="Time remaining before redirect"
              >
                <div className="pr-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>

              <div className="pr-actions">
                <button type="button" className="pr-btn pr-btn-primary" onClick={goNow}>
                  Go to recruit.placerep.ai now
                  <span aria-hidden="true">&rarr;</span>
                </button>
                <a className="pr-btn pr-btn-secondary" href={REDIRECT_URL}>
                  Open in new tab
                </a>
              </div>

              <p className="pr-help">
                If you are not redirected automatically,{' '}
                <a href={REDIRECT_URL}>click here to go to recruit.placerep.ai</a>.
              </p>

              <noscript>
                <div className="pr-noscript">
                  JavaScript is disabled. Please{' '}
                  <a href={REDIRECT_URL}>click here to visit recruit.placerep.ai</a>.
                </div>
              </noscript>
            </div>
          </div>
        </section>

        <section className="pr-section" aria-labelledby="pr-whats-new">
          <div className="pr-section-inner">
            <h2 id="pr-whats-new" className="pr-h2">What&rsquo;s new on PlaceRep.ai</h2>
            <p className="pr-section-sub">
              The same team, a smarter platform. Here&rsquo;s what to expect on the new domain.
            </p>
            <div className="pr-grid">
              {features.map((f) => (
                <article className="pr-feat" key={f.title}>
                  <FeatureIcon>{f.icon}</FeatureIcon>
                  <h3 className="pr-feat-title">{f.title}</h3>
                  <p className="pr-feat-text">{f.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <RedirectFooter />
    </div>
  );
}
